import { ArrowLeftRight, RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { officialTcgCards } from "../data/site";
import { getLinkedTag } from "../utils/rewards";
import {
  acceptTrade,
  cancelTrade,
  cardById,
  fetchCloudCollection,
  fetchTrades,
  getLocalCollection,
  postTrade,
  variantOf,
} from "../utils/tcg";

const shortName = (id) => {
  const card = cardById(id);
  if (!card) return id;
  const variant = variantOf(card);
  return `${card.name.split(",")[0]}${variant ? ` · ${variant}` : ""}`;
};

/* The trade board — runners post offers ("my dupe for your dupe") and others
   accept them. Only duplicates (x2 or more) can move; you always keep one. */
export function TcgTrades() {
  const tag = getLinkedTag();
  const [trades, setTrades] = useState(null);
  const [collection, setCollection] = useState(getLocalCollection());
  const [give, setGive] = useState("");
  const [want, setWant] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    fetchTrades()
      .then(setTrades)
      .catch(() => setTrades([]));
    fetchCloudCollection().then((cloud) => {
      if (cloud?.cards) setCollection(cloud.cards);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const dupes = officialTcgCards.filter((card) => (collection[card.id] || 0) >= 2);
  const wantables = officialTcgCards.filter((card) => card.id !== give);

  if (!tag) {
    return (
      <div className="trade-board" data-testid="trade-board">
        <div className="trade-locked" data-testid="trade-locked-notice">
          <h3>THE BOARD ONLY DEALS WITH KNOWN RUNNERS.</h3>
          <p>
            Trades move real collection units between vaults, so the board needs your runner tag.{" "}
            <Link to="/account">Sign in at the account desk</Link> to link your tag, then come back with dupes to swap.
          </p>
        </div>
      </div>
    );
  }

  const submitTrade = async () => {
    if (!give || !want || busy) return;
    setBusy(true);
    try {
      await postTrade(give, want);
      toast.success(`OFFER POSTED — ${shortName(give)} FOR ${shortName(want)}.`);
      setGive("");
      setWant("");
      load();
    } catch (error) {
      toast.error(error.message);
    }
    setBusy(false);
  };

  const accept = async (trade) => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await acceptTrade(trade.id);
      toast.success(`TRADE COMPLETE — ${shortName(data.received)} IS YOURS.`);
      setCollection(getLocalCollection());
      load();
    } catch (error) {
      toast.error(error.message);
      load();
    }
    setBusy(false);
  };

  const cancel = async (trade) => {
    if (busy) return;
    setBusy(true);
    try {
      await cancelTrade(trade.id);
      toast.success("OFFER PULLED FROM THE BOARD.");
      load();
    } catch (error) {
      toast.error(error.message);
    }
    setBusy(false);
  };

  return (
    <div className="trade-board" data-testid="trade-board">
      <div className="trade-board-head">
        <h3>
          <ArrowLeftRight size={16} /> THE TRADE BOARD
        </h3>
        <button type="button" onClick={load} data-testid="trade-refresh" aria-label="Refresh trade board">
          <RefreshCw size={14} /> REFRESH
        </button>
      </div>
      <p className="trade-board-copy">
        Post a duplicate you can spare and name the unit you want back. Only dupes (×2 or more) can move —
        your last copy never leaves the vault. Signed in as <strong>{tag}</strong>.
      </p>

      <div className="trade-form" data-testid="trade-form">
        <label>
          I OFFER
          <select value={give} onChange={(e) => setGive(e.target.value)} data-testid="trade-give-select">
            <option value="">— pick a dupe —</option>
            {dupes.map((card) => (
              <option key={card.id} value={card.id}>
                {shortName(card.id)} (×{collection[card.id]})
              </option>
            ))}
          </select>
        </label>
        <ArrowLeftRight size={16} className="trade-form-icon" />
        <label>
          I WANT
          <select value={want} onChange={(e) => setWant(e.target.value)} data-testid="trade-want-select">
            <option value="">— any unit —</option>
            {wantables.map((card) => (
              <option key={card.id} value={card.id}>
                {shortName(card.id)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="trade-post-button"
          onClick={submitTrade}
          disabled={!give || !want || busy}
          data-testid="trade-post-button"
        >
          POST OFFER
        </button>
      </div>
      {dupes.length === 0 && (
        <p className="trade-empty-note" data-testid="trade-no-dupes">
          NO DUPES IN YOUR VAULT YET — CRACK CRATES UNTIL SOMETHING LANDS TWICE.
        </p>
      )}

      <div className="trade-list" data-testid="trade-list">
        {trades === null ? (
          <p className="trade-empty-note">SCANNING THE BOARD…</p>
        ) : trades.length === 0 ? (
          <p className="trade-empty-note" data-testid="trade-empty">THE BOARD IS BARE — POST THE FIRST OFFER.</p>
        ) : (
          trades.map((trade) => {
            const mine = trade.tag === tag;
            const canAccept = !mine && (collection[trade.want] || 0) >= 2;
            return (
              <div key={trade.id} className={`trade-row${mine ? " is-mine" : ""}`} data-testid={`trade-row-${trade.id}`}>
                <img src={cardById(trade.give)?.image} alt={`${shortName(trade.give)} card`} className="trade-card-thumb" />
                <ArrowLeftRight size={14} className="trade-row-icon" />
                <img src={cardById(trade.want)?.image} alt={`${shortName(trade.want)} card`} className="trade-card-thumb" />
                <div className="trade-row-copy">
                  <strong>{trade.tag}</strong>
                  <span>
                    OFFERS {shortName(trade.give)} · WANTS {shortName(trade.want)}
                  </span>
                </div>
                {mine ? (
                  <button type="button" className="trade-cancel-button" onClick={() => cancel(trade)} disabled={busy} data-testid={`trade-cancel-${trade.id}`}>
                    <X size={13} /> PULL OFFER
                  </button>
                ) : (
                  <button
                    type="button"
                    className="trade-accept-button"
                    onClick={() => accept(trade)}
                    disabled={!canAccept || busy}
                    data-testid={`trade-accept-${trade.id}`}
                    title={canAccept ? "Swap your dupe for theirs" : `You need ×2 ${shortName(trade.want)} to accept`}
                  >
                    {canAccept ? "ACCEPT SWAP" : `NEED ×2 ${shortName(trade.want)}`}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
